import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient.jsx";
import { useCart } from "../../../shared/contexts/CartContext.jsx";
import { usePaymentGateway } from "../usePaymentGateway.js";
import { hasActiveSubscription } from "../subscription/subscriptionStatus.js";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { AlertTriangle, RefreshCw, Wifi, Clock } from "lucide-react";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);

const METHOD_LABELS = { pix: "PIX", cartao: "Cartão" };

/** Retorna ícone e mensagem de acordo com o código de erro do gateway */
function ErrorDetail({ code, message }) {
  const details = {
    TIMEOUT: { icon: Clock, hint: "A conexão demorou demais." },
    NETWORK: { icon: Wifi, hint: "Verifique sua internet." },
    AUTH: { icon: AlertTriangle, hint: "Sua sessão expirou." },
    SERVER: { icon: AlertTriangle, hint: "Serviço temporariamente indisponível." },
    CONFIG: { icon: AlertTriangle, hint: "Erro de configuração. Contate o suporte." },
  };
  const cfg = details[code] ?? { icon: AlertTriangle, hint: null };
  const Icon = cfg.icon;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <Icon size={15} className="mt-0.5 shrink-0" />
      <span>
        {message}
        {cfg.hint && code !== "CONFIG" ? (
          <span className="ml-1 text-muted-foreground">({cfg.hint})</span>
        ) : null}
      </span>
    </div>
  );
}

export default function PagamentoPage() {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { gateway, provider } = usePaymentGateway();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);   // { message, code }
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [mockTransaction, setMockTransaction] = useState(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const isMock = provider === "mock";
  const hasPacoteInCart = cart.some((item) => item.tipo === "pacote");

  const total = useMemo(
    () => cart.reduce((acc, item) => acc + (item.preco || 0) * (item.quantidade || 1), 0),
    [cart],
  );

  useEffect(() => {
    async function loadUser() {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      setUser(currentUser || null);

      if (!currentUser || !hasPacoteInCart) {
        setSubscriptionRequired(false);
        return;
      }

      const ativa = await hasActiveSubscription({
        userId: currentUser.id,
        email: currentUser.email,
      });

      setSubscriptionRequired(!ativa);
    }

    loadUser();
  }, [hasPacoteInCart]);

  async function handleCheckout() {
    if (!user && !isMock) {
      navigate("/login");
      return;
    }
    if (!cart.length) {
      setError({ message: "Seu carrinho está vazio.", code: null });
      return;
    }

    if (hasPacoteInCart && subscriptionRequired) {
      setError({
        message: "Para comprar pacotes de horas, escolha uma assinatura ativa antes de continuar.",
        code: null,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const mockUserId =
        localStorage.getItem("tesourinhos_mock_user_id") ||
        `mock-user-${Math.random().toString(36).slice(2, 8)}`;

      if (isMock) {
        localStorage.setItem("tesourinhos_mock_user_id", mockUserId);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      // Sessão expirada em modo real
      if (!session?.access_token && !isMock) {
        setError({
          message: "Sua sessão expirou. Faça login novamente para continuar.",
          code: "AUTH",
        });
        setTimeout(() => navigate("/login"), 2500);
        return;
      }

      const result = await gateway.createCheckoutSession({
        userId: user?.id || mockUserId,
        userEmail: user?.email || `${mockUserId}@mock.local`,
        items: cart,
        paymentMethod,
        accessToken: session?.access_token,
      });

      if (result.redirectUrl) {
        // Redireciona para o Mercado Pago — não limpa carrinho aqui,
        // pois o usuário pode cancelar. A limpeza ocorre no PagamentoStatus.
        window.location.href = result.redirectUrl;
        return;
      }

      if (result.transaction) {
        setMockTransaction(result.transaction);
      }
    } catch (checkoutError) {
      setError({
        message: checkoutError.message || "Falha ao iniciar pagamento.",
        code: checkoutError.code || null,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleMockStatus(status) {
    if (!mockTransaction) return;
    const updated = await gateway.updatePaymentStatus({
      transactionId: mockTransaction.id,
      status,
    });
    setMockTransaction(updated);
    if (status === "aprovado") {
      clearCart();
      setTimeout(() => navigate("/pagamento/sucesso"), 800);
    }
    if (status === "falhou") {
      setTimeout(() => navigate("/pagamento/falha"), 800);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Pagamento
      </p>
      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-2xl font-bold text-foreground">Finalizar compra</h1>
        {isMock && <Badge variant="secondary">modo desenvolvimento</Badge>}
      </div>

      {/* Carrinho vazio */}
      {!cart.length && !mockTransaction && (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">Seu carrinho está vazio.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Card>
      )}

      {/* Resumo do pedido */}
      {!!cart.length && !mockTransaction && (
        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold text-foreground">Resumo do pedido</h2>
          <div className="space-y-2">
            {cart.map((item) => (
              <div
                key={`${item.tipo}-${item.id}`}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground">
                  {item.quantidade || 1}x {item.nome}
                </span>
                <span className="font-semibold text-foreground">
                  {formatCurrency((item.preco || 0) * (item.quantidade || 1))}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
            <span className="text-sm font-semibold text-muted-foreground">Total</span>
            <strong className="text-lg text-foreground">{formatCurrency(total)}</strong>
          </div>

          {/* Seleção de método */}
          <div className="mt-5 flex gap-2">
            {["pix", "cartao"].map((method) => (
              <button
                key={method}
                onClick={() => setPaymentMethod(method)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  paymentMethod === method
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                {METHOD_LABELS[method]}
              </button>
            ))}
          </div>

          {/* Área de erro com detalhes e retry */}
          {error && (
            <>
              <ErrorDetail code={error.code} message={error.message} />
              {error.code !== "AUTH" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 gap-1.5"
                  onClick={() => { setError(null); handleCheckout(); }}
                  disabled={loading}
                >
                  <RefreshCw size={13} />
                  Tentar novamente
                </Button>
              )}
            </>
          )}

          <Button
            className="mt-5 w-full"
            onClick={handleCheckout}
            disabled={loading || !cart.length || (hasPacoteInCart && subscriptionRequired)}
          >
            {loading ? "Processando..." : "Continuar pagamento"}
          </Button>

          {hasPacoteInCart && subscriptionRequired && (
            <Button
              variant="outline"
              className="mt-3 w-full"
              onClick={() => navigate("/escolher-assinatura?next=/pagamento")}
            >
              Escolher assinatura primeiro
            </Button>
          )}

          <p className="mt-3 text-center text-xs text-muted-foreground">
            Ambiente seguro. Você será redirecionado para concluir o pagamento.
          </p>
        </Card>
      )}

      {/* Transação mock criada */}
      {mockTransaction && (
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-foreground">Pagamento criado</h3>
            <Badge
              variant={
                mockTransaction.status === "aprovado"
                  ? "default"
                  : mockTransaction.status === "falhou"
                  ? "destructive"
                  : "secondary"
              }
            >
              {mockTransaction.status}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-4">ID: {mockTransaction.id}</p>

          {mockTransaction.status === "pendente" && (
            <div className="flex gap-3 mb-4">
              <Button size="sm" onClick={() => handleMockStatus("aprovado")}>
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleMockStatus("falhou")}
              >
                Reprovar
              </Button>
            </div>
          )}

          <Button variant="outline" onClick={() => navigate("/meus-pagamentos")}>
            Ver histórico de pagamentos
          </Button>
        </Card>
      )}
    </div>
  );
}
